import { ref, computed } from 'vue';

import { defineStore } from 'pinia';

import * as Sentry from '@sentry/vue';
import { ElNotification } from 'element-plus';

import { usdFromCents, flagCode } from '@/@core/utils/formatters';
import { featureDefinitions } from '@/config/billing/featureDefinitions';
import { planDefinitions } from '@/config/billing/planDefinitions';
import { consumeNewBusinessProfile } from '@/helpers/newBusinessProfiles';
import * as billingApi from '@/services/billingApi';
import { useAccountStore } from '@/stores/account';
import { useReferralProgram } from '@/stores/referralProgram';
import { trackEvent } from '@/utils/ga';


/**
 * Map a backend plan name to a planDefinitions key.
 */
const mapPlanKey = (backendPlan, backendStatus) => {
  if (backendStatus === 'trial') return 'trial';
  if (backendStatus === 'trial_expired') return 'trialExpired';
  if (!backendPlan) return backendStatus === 'active' ? 'basic' : 'trial';
  // Stripe product name; rows synced before the 2026-10 rename still say 'Basic Plan'.
  const map = {
    'Founders Plan': 'basic',
    Founders: 'basic',
    'Basic Plan': 'basic',
    Enterprise: 'enterprise',
    Trial: 'trial',
  };
  return map[backendPlan] || 'basic';
};

// The billing engine answers GET /status/{external_id} with 404 until the customer is registered.
const isCustomerNotFound = (error) => error?.response?.status === 404;

const normalizePromoCode = (value) => String(value || '').trim().toUpperCase();

// Coupons and referrals are redeemed through one shared endpoint, so the backend's
// free-text messages may name the wrong entity for the tab the user is on (e.g. a
// "Referral code 'X' not found" reason surfaced inside Coupon Management). These
// helpers re-label any code-noun in a user-facing string to match the active flow.
const codeNounFor = (kind) => (kind === 'referral' ? 'referral code' : 'coupon code');

const relabelCodeNoun = (text, kind) => {
  if (!text) return text;
  const target = codeNounFor(kind);
  return String(text).replace(/\b(?:referral|coupon|promo)\s+codes?\b/gi, (match) => {
    const replacement = /codes\b/i.test(match) ? `${target}s` : target;
    return /^[A-Z]/.test(match) ? replacement.charAt(0).toUpperCase() + replacement.slice(1) : replacement;
  });
};

export const useBillingStore = defineStore('billing', () => {
  // State
  const currentPlan = ref('trial'); // trial, basic, enterprise
  const subscriptionStatus = ref('trial'); // active, past_due, canceled, trial, trial_expired, no_subscription
  const trialEndDate = ref(null);
  const gracePeriodEnd = ref(null);
  const currentPeriodEnd = ref(null);
  const cancelAtPeriodEnd = ref(false);
  const cancelAt = ref(null);

  // Active add-ons (mapped from backend)
  const activeAddOns = ref([]);

  // Discount / coupon applied to the subscription
  const discount = ref(null);

  // Metered (1%-of-ad-spend) info from /status.metered. All three are null when
  // metered billing is off, during trial / no-subscription, or (for the estimate)
  // on a transient ClickHouse/FX error — the backend omits the field in each case.
  const meteredEstimate = ref(null); // current_period_estimate { period_start, period_end, mtd_usd_cents, projected_total_usd_cents }
  const meteredLastBilled = ref(null); // last_billed_period { period_start, period_end, total_billed_usd_cents, breakdown_by_marketplace[] }
  const meteredRateBps = ref(null); // e.g. 100 = 1.00%
  const meteredStart = ref(null); // ISO date the 1%-of-ad-spend fee begins (first-cycle deferral); null pre-#94 backend or no subscription

  // Billing history
  const billingHistory = ref(null);
  const billingHistoryLoading = ref(false);

  // Loading states
  const isLoading = ref(false);
  const isProcessing = ref(false);
  // Tracks whether initializeBillingData has completed at least once for the
  // current business profile. Used by ensureInitialized() so global consumers
  // (e.g. trial-expiry alert builder) can lazy-init outside of /billing.
  const hasInitialized = ref(false);
  let initPromise = null;
  let initializedFor = null;

  // Last error from any action
  const lastError = ref(null);

  const clearError = () => {
    lastError.value = null;
  };

  const resetBillingHistory = () => {
    billingHistory.value = null;
    billingHistoryLoading.value = false;
  };

  // Getters/Computed
  const accountStore = useAccountStore();

  const businessProfileId = computed(() => accountStore.currentBusinessProfile?.id);

  const isTrialActive = computed(() => {
    if (currentPlan.value !== 'trial' || !trialEndDate.value) return false;
    return new Date() < new Date(trialEndDate.value);
  });

  const trialDaysRemaining = computed(() => {
    if (!isTrialActive.value) return 0;
    const now = new Date();
    const end = new Date(trialEndDate.value);
    const diffTime = end - now;
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  });

  // Fraction of the trial ELAPSED, not remaining — the bars this feeds are
  // labelled "Trial Progress" / "Trial Ending Soon", so they must fill as the
  // trial runs out. (Previously returned days-remaining/total, which rendered a
  // near-empty bar on the last days of the trial.)
  const trialProgressPct = computed(() => {
    const total = planDefinitions.trial.trialDuration;
    if (!total) return 0;
    const elapsed = total - trialDaysRemaining.value;
    return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  });

  const isInGracePeriod = computed(() => {
    if (!gracePeriodEnd.value) return false;
    return new Date() < new Date(gracePeriodEnd.value);
  });

  const graceDaysRemaining = computed(() => {
    if (!isInGracePeriod.value) return 0;
    const diffTime = new Date(gracePeriodEnd.value) - new Date();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  });

  const currentPlanData = computed(() => planDefinitions[currentPlan.value] || planDefinitions.trial);

  const monthlySubscriptionCost = computed(() => {
    let total = currentPlanData.value.price || 0;

    activeAddOns.value.forEach((addOn) => {
      const feature = featureDefinitions[addOn.featureId];
      if (feature) {
        total += feature.price * addOn.quantity;
      }
    });

    return total;
  });

  // Projected current-cycle metered fee (USD). Drives the cost widget + the page
  // top-line total. 0 during trial / no-subscription / transient CH-FX error.
  const estimatedMonthlyUsageCost = computed(() =>
    meteredEstimate.value ? usdFromCents(meteredEstimate.value.projected_total_usd_cents) : 0,
  );

  // Metered fee accrued so far this cycle (USD).
  const meteredMtdCost = computed(() =>
    meteredEstimate.value ? usdFromCents(meteredEstimate.value.mtd_usd_cents) : 0,
  );

  // Metering rate as a percent (100 bps → 1). null when metered info is absent.
  const meteredRatePct = computed(() =>
    meteredRateBps.value != null ? meteredRateBps.value / 100 : null,
  );

  // Last billed cycle total (USD).
  const meteredLastBilledTotal = computed(() =>
    meteredLastBilled.value ? usdFromCents(meteredLastBilled.value.total_billed_usd_cents) : 0,
  );

  // Per-marketplace breakdown of the LAST BILLED cycle.
  const meteredBreakdown = computed(() =>
    (meteredLastBilled.value?.breakdown_by_marketplace ?? []).map((m) => ({
      marketplaceId: m.marketplace_id,
      code: m.code,
      flag: flagCode(m.code),
      currency: m.native_currency,
      nativeAmount: usdFromCents(m.native_amount_cents), // native major units (e.g. £0.36)
      usd: usdFromCents(m.billed_amount_cents),
    })),
  );

  // Per-marketplace breakdown of the CURRENT cycle estimate. Same source shape as
  // the last-billed breakdown, but each row carries the MTD and projected fee for
  // that marketplace (their `projected` values sum to estimatedMonthlyUsageCost).
  // Empty when the backend omits the breakdown on the current-period estimate.
  const meteredEstimateBreakdown = computed(() =>
    (meteredEstimate.value?.breakdown_by_marketplace ?? []).map((m) => ({
      marketplaceId: m.marketplace_id,
      code: m.code,
      flag: flagCode(m.code),
      currency: m.native_currency,
      nativeAmount: usdFromCents(m.native_amount_cents), // native ad spend (MTD)
      mtd: usdFromCents(m.mtd_usd_cents),
      projected: usdFromCents(m.projected_usd_cents),
    })),
  );

  const totalEstimatedMonthlyCost = computed(
    () => monthlySubscriptionCost.value + estimatedMonthlyUsageCost.value,
  );

  // Dunning: the customer HAS a subscription but its renewal payment failed
  // (or it's paused/incomplete). They must fix payment — NOT subscribe again
  // (backend /checkout rejects these statuses with 409).
  const DUNNING_STATUSES = new Set(['past_due', 'unpaid', 'incomplete', 'paused']);
  const isDunning = computed(() => DUNNING_STATUSES.has(subscriptionStatus.value));

  // True only for genuinely-no-live-subscription states (trial, trial_expired,
  // no_subscription, canceled). Dunning customers have a subscription — showing
  // them "Subscribe Now" was a dead-end 409 (webapp#1080 F4), and hiding the
  // Billing tab behind this flag locked them out of the very invoice they need
  // to pay.
  const needsSubscription = computed(
    () => subscriptionStatus.value !== 'active' && !isDunning.value,
  );

  const availableAddOns = computed(() => {
    const plan = currentPlanData.value;
    if (plan.type === 'enterprise') return [];

    const availableFeatureIds = plan.features.availableAddOns || [];
    return availableFeatureIds.map((featureId) => ({
      ...featureDefinitions[featureId],
      isActive: activeAddOns.value.some((addOn) => addOn.featureId === featureId),
    }));
  });

  const discountLabel = computed(() => {
    if (!discount.value) return '';
    if (discount.value.discount_type === 'percent_off') return `${discount.value.discount_value}% off`;
    return `$${(discount.value.discount_value / 100).toFixed(2)} off`;
  });

  const discountDurationLabel = computed(() => {
    const d = discount.value;
    if (!d) return '';
    if (d.discount_duration === 'forever') return 'Forever';
    if (d.discount_duration === 'once') return 'First invoice only';
    if (d.discount_duration === 'repeating' && d.discount_end) {
      return `Until ${new Date(d.discount_end).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`;
    }
    return 'Limited time';
  });

  // SP2 resolved-discount helpers (null/[] under the SP1 single-code shape).
  const appliedCodes = computed(() => discount.value?.applied_codes ?? []);

  // The first-invoice effective %, present only when the first invoice resolves
  // higher than the recurring discount_value (e.g. a `once` code stacked on top).
  const firstInvoiceDiscountValue = computed(() => discount.value?.first_invoice_value ?? null);

  const hasFirstInvoiceUplift = computed(() => {
    const d = discount.value;
    return !!d && d.first_invoice_value != null && d.first_invoice_value > (d.discount_value ?? 0);
  });

  const applyDiscount = (amount) => {
    if (!discount.value) return amount;
    if (discount.value.discount_type === 'percent_off')
      return amount * (1 - discount.value.discount_value / 100);
    return Math.max(0, amount - discount.value.discount_value / 100);
  };

  // --- Helpers ---

  // Post-checkout unlock: the route guard keys on currentBusinessProfile.plan
  // (gateState/gateReason from the manager payload), which goes stale the moment
  // Stripe activates the subscription — without a re-sync a just-paid user would
  // stay route-blocked until a hard reload. Runs only while the plan still reads
  // gated, and never concurrently (syncBusinessProfiles dedupes too).
  let planResyncInFlight = false;
  const resyncGatedPlan = () => {
    const gateState = accountStore.currentBusinessProfile?.plan?.gateState;
    if (gateState !== 'soft_gate' && gateState !== 'hard_gate') return;
    if (planResyncInFlight) return;
    planResyncInFlight = true;
    Promise.resolve(accountStore.syncBusinessProfiles())
      .catch((error) => {
        console.error('Failed to refresh business profiles after billing activation:', error);
      })
      .finally(() => {
        planResyncInFlight = false;
      });
  };

  /**
   * Map a BillingStatusResponse to store state.
   */
  const applyBillingStatus = (data) => {
    subscriptionStatus.value = data.status;
    currentPlan.value = mapPlanKey(data.plan, data.status);
    trialEndDate.value = data.trial_end || null;
    gracePeriodEnd.value = data.grace_period_end || null;
    currentPeriodEnd.value = data.current_period_end || null;
    cancelAtPeriodEnd.value = data.cancel_at_period_end || false;
    cancelAt.value = data.cancel_at || null;

    // Map discount. SP1 shape = single coupon { discount_type, discount_value,
    // discount_duration, discount_end }. SP2 resolved shape adds applied_codes[]
    // and first_invoice_value on the same object — they flow through unchanged and
    // are surfaced via appliedCodes / firstInvoiceDiscountValue below.
    discount.value = data.discount ?? null;

    // Map add-ons
    activeAddOns.value = (data.add_ons || []).map((addon) => ({
      featureId: addon.feature_id || 'unknown',
      quantity: addon.quantity || 1,
      subscriptionItemId: addon.subscription_item_id,
      price: (addon.unit_amount || 0) / 100, // cents → dollars
      name: addon.product_name,
      monthlyCost: (addon.monthly_cost || 0) / 100,
    }));

    // Metered (1% of ad spend) info. The whole `metered` block is omitted when the
    // backend flag is off; individual sub-fields are omitted per the rules above.
    const metered = data.metered ?? null;
    meteredEstimate.value = metered?.current_period_estimate ?? null;
    meteredLastBilled.value = metered?.last_billed_period ?? null;
    meteredRateBps.value = metered?.rate_bps ?? null;
    meteredStart.value = metered?.metered_start ?? null;

    // Covers the checkout-success fetch, the webhook re-poll below, and any
    // later refresh that comes back active while the plan payload still gates.
    if (data.status === 'active') resyncGatedPlan();
  };

  /**
   * Back to the state of a business profile with no billing customer yet (the trial defaults), so
   * a profile without a row never shows the previous profile's plan.
   */
  const resetBillingStatus = () => {
    currentPlan.value = 'trial';
    subscriptionStatus.value = 'trial';
    trialEndDate.value = null;
    gracePeriodEnd.value = null;
    currentPeriodEnd.value = null;
    cancelAtPeriodEnd.value = false;
    cancelAt.value = null;
    activeAddOns.value = [];
    discount.value = null;
    meteredEstimate.value = null;
    meteredLastBilled.value = null;
    meteredRateBps.value = null;
    meteredStart.value = null;
  };

  // --- Actions ---

  /**
   * Fetch billing status from backend. On 404 → register customer → re-fetch.
   */
  const initializeBillingData = async (bpId) => {
    let isCheckoutSuccess = false;
    try {
      isLoading.value = true;
      const externalId = billingApi.toExternalId(bpId);

      // Check for checkout redirect
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('checkout') === 'success') {
          isCheckoutSuccess = true;
          trackEvent('subscription_success', { business_profile_id: bpId });
          // 'basic' is the only self-serve plan today (billing/Index.vue only sends 'basic').
          const sessionId = params.get('session_id');
          // Billing sends checkout_mode ('setup' or 'subscription'); anything else is left out, never guessed.
          const checkoutMode = params.get('checkout_mode');
          trackEvent('purchase', {
            item_type: 'plan',
            plan_id: 'basic',
            ...(sessionId ? { transaction_id: sessionId } : {}),
            ...(checkoutMode === 'setup' || checkoutMode === 'subscription'
              ? { checkout_mode: checkoutMode }
              : {}),
          });
          // Clean up query params without reload
          const url = new URL(window.location);
          url.searchParams.delete('checkout');
          url.searchParams.delete('session_id');
          url.searchParams.delete('checkout_mode');
          window.history.replaceState({}, '', url);
        }
      }

      // 404 = no billing customer yet (a brand-new business profile before its trial row exists).
      // Not an error: keep the defaults (trial) and let the next refresh pick the row up.
      let data = null;
      let registered = false;
      const registerTrialCustomer = async () => {
        // Creates the Stripe customer + trial
        await billingApi.registerCustomer(externalId);
        trackEvent('begin_trial', { business_profile_id: bpId });
      };

      // A business profile created a moment ago has no billing row yet, so register it first and
      // skip the status request that would only answer 404. If registering fails, fall through to
      // the ordinary status-first path below.
      if (consumeNewBusinessProfile(bpId)) {
        try {
          await registerTrialCustomer();
          registered = true;
        } catch {
          registered = false;
        }
      }

      try {
        const resp = await billingApi.getBillingStatus(externalId);
        data = resp.data;
      } catch (err) {
        if (!isCustomerNotFound(err)) throw err;
        if (!registered) {
          // First visit: register customer, then re-fetch
          await registerTrialCustomer();
          try {
            const resp = await billingApi.getBillingStatus(externalId);
            data = resp.data;
          } catch (refetchErr) {
            if (!isCustomerNotFound(refetchErr)) throw refetchErr;
          }
        }
      }

      // Still no customer after registering (404 again): this profile has no billing row, so drop
      // whatever the previous profile left in the store instead of showing its plan.
      if (data) applyBillingStatus(data);
      else resetBillingStatus();

      // Stripe webhooks are async — poll until status becomes active (max 3 attempts).
      if (isCheckoutSuccess && data && data.status !== 'active') {
        let attempts = 0;
        const poll = async () => {
          if (attempts >= 3) return;
          attempts++;
          await refreshBillingStatus();
          if (subscriptionStatus.value !== 'active') {
            setTimeout(poll, 3000);
          }
        };
        setTimeout(poll, 3000);
      }
    } catch (error) {
      console.error('Failed to initialize billing data:', error);
      lastError.value = error.response?.data?.detail || error.message || 'Failed to load billing data.';
    } finally {
      isLoading.value = false;
      hasInitialized.value = true;
      initializedFor = bpId;
    }
  };

  // Lazy init for consumers outside /billing (e.g. global alert builder).
  // De-dupes concurrent callers via initPromise. Re-inits when the business
  // profile changes so cached trial/grace fields don't leak across profiles.
  let initializingFor = null;
  const ensureInitialized = (bpId) => {
    if (initPromise && initializingFor === bpId) return initPromise;
    if (hasInitialized.value && initializedFor === bpId) return Promise.resolve();
    hasInitialized.value = false;
    initializingFor = bpId;
    initPromise = initializeBillingData(bpId).finally(() => {
      initPromise = null;
      initializingFor = null;
    });
    return initPromise;
  };

  /**
   * Re-fetch billing status from backend.
   */
  const refreshBillingStatus = async () => {
    if (!businessProfileId.value) return;
    try {
      const externalId = billingApi.toExternalId(businessProfileId.value);
      const resp = await billingApi.getBillingStatus(externalId);
      applyBillingStatus(resp.data);
    } catch (error) {
      // No billing customer yet: nothing to refresh, and not an error.
      if (isCustomerNotFound(error)) return;
      console.error('Failed to refresh billing status:', error);
      lastError.value = error.response?.data?.detail || error.message || 'Failed to refresh billing status.';
    }
  };

  /**
   * Start Stripe Checkout flow for a paid plan.
   */
  const convertTrialToPaid = async (planId = 'basic') => {
    // Fires on click intent, independent of whether checkout succeeds — the
    // Stripe redirect (or an API failure) can still lose the customer, so this
    // is the only reliable signal that someone tried to subscribe at all.
    trackEvent('subscribe_click', { business_profile_id: businessProfileId.value, plan_id: planId });
    try {
      isProcessing.value = true;

      const externalId = billingApi.toExternalId(businessProfileId.value);
      const resp = await billingApi.createCheckout(externalId, planId);
      const checkoutUrl = resp.data.checkout_url;

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
      return { success: true };
    } catch (error) {
      console.error('Failed to start checkout:', error);
      const msg = error.response?.data?.detail || error.message;
      // No response: the call never reached the server, so nothing else toasts it.
      // Leave lastError unset — the billing page notifies on it and would stack a second toast.
      if (!error?.response) {
        ElNotification({
          title: 'Action Failed',
          message: 'Couldn\'t start checkout. Please try again.',
          type: 'error',
          duration: 6000,
        });
        Sentry.captureException(error, { tags: { area: 'billing-checkout', code: error?.code } });
      } else {
        lastError.value = msg;
      }
      return { success: false, error: msg };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Add an add-on via the backend.
   * If the add-on already exists, updates quantity via PATCH instead of creating a new one.
   */
  const addAddOn = async (featureId, quantity = 1) => {
    try {
      isProcessing.value = true;

      const feature = featureDefinitions[featureId];
      if (!feature || !feature.available) {
        throw new Error('Feature not available');
      }

      const existing = activeAddOns.value.find((a) => a.featureId === featureId);

      if (existing) {
        // Add-on already exists — update quantity via PATCH
        const newQuantity = existing.quantity + quantity;
        await billingApi.updateAddOn(existing.subscriptionItemId, newQuantity);

        // Optimistic update: update quantity + cost locally
        const idx = activeAddOns.value.findIndex((a) => a.featureId === featureId);
        if (idx !== -1) {
          activeAddOns.value[idx] = {
            ...activeAddOns.value[idx],
            quantity: newQuantity,
            monthlyCost: newQuantity * (activeAddOns.value[idx].price || feature.price),
          };
        }
      } else {
        // New add-on — create via POST
        const externalId = billingApi.toExternalId(businessProfileId.value);
        await billingApi.addAddOn(externalId, featureId, quantity);

        // Optimistic update: push new add-on entry locally
        activeAddOns.value = [
          ...activeAddOns.value,
          {
            featureId,
            quantity,
            price: feature.price,
            name: feature.name,
            monthlyCost: feature.price * quantity,
            subscriptionItemId: null, // updated after refresh
          },
        ];
      }

      // Schedule delayed refresh to sync with webhook-confirmed state (~1s gives Stripe webhook time to sync)
      setTimeout(() => refreshBillingStatus(), 1000);
      return { success: true };
    } catch (error) {
      console.error('Failed to add add-on:', error);
      const msg = error.response?.data?.detail || error.message;
      lastError.value = msg;
      return { success: false, error: msg };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Remove an add-on via the backend.
   */
  const removeAddOn = async (featureId) => {
    try {
      isProcessing.value = true;

      const addOn = activeAddOns.value.find((a) => a.featureId === featureId);
      if (!addOn) {
        throw new Error('Add-on not found');
      }

      await billingApi.removeAddOn(addOn.subscriptionItemId);

      // Optimistic update: remove from local list immediately
      activeAddOns.value = activeAddOns.value.filter((a) => a.featureId !== featureId);

      // Schedule delayed refresh to sync with webhook-confirmed state
      setTimeout(() => refreshBillingStatus(), 1000);
      return { success: true };
    } catch (error) {
      console.error('Failed to remove add-on:', error);
      const msg = error.response?.data?.detail || error.message;
      lastError.value = msg;
      return { success: false, error: msg };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Schedule subscription cancellation at period end.
   */
  const cancelSubscription = async () => {
    try {
      isProcessing.value = true;
      const externalId = billingApi.toExternalId(businessProfileId.value);
      const resp = await billingApi.cancelSubscription(externalId);
      // Apply response directly — webhook may not have synced yet
      cancelAtPeriodEnd.value = resp.data.cancel_at_period_end;
      currentPeriodEnd.value = resp.data.current_period_end || currentPeriodEnd.value;
      return { success: true };
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      const msg = error.response?.data?.detail || error.message;
      lastError.value = msg;
      return { success: false, error: msg };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Undo pending subscription cancellation.
   */
  const resumeSubscription = async () => {
    try {
      isProcessing.value = true;
      const externalId = billingApi.toExternalId(businessProfileId.value);
      const resp = await billingApi.resumeSubscription(externalId);
      // Apply response directly — webhook may not have synced yet
      cancelAtPeriodEnd.value = resp.data.cancel_at_period_end;
      currentPeriodEnd.value = resp.data.current_period_end || currentPeriodEnd.value;
      return { success: true };
    } catch (error) {
      console.error('Failed to resume subscription:', error);
      const msg = error.response?.data?.detail || error.message;
      lastError.value = msg;
      return { success: false, error: msg };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Fetch billing history (invoices, charges, payments, disputes).
   */
  const fetchBillingHistory = async () => {
    try {
      billingHistoryLoading.value = true;
      const resp = await billingApi.getBillingHistory();
      billingHistory.value = resp.data;
    } catch (error) {
      console.error('Failed to fetch billing history:', error);
      lastError.value = error.response?.data?.detail || error.message || 'Failed to load billing history.';
    } finally {
      billingHistoryLoading.value = false;
    }
  };

  /**
   * Create a Stripe Billing Portal session and redirect the user to it.
   */
  const redirectToBillingPortal = async () => {
    try {
      isProcessing.value = true;
      const externalId = billingApi.toExternalId(businessProfileId.value);
      const resp = await billingApi.createBillingPortal(externalId);
      window.location.href = resp.data.portal_url;
    } catch (error) {
      console.error('Failed to create billing portal:', error);
      const msg = error.response?.data?.detail || error.message;
      lastError.value = msg;
      return { success: false, error: msg };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Redeem a promo/referral code via manager API and return UI-friendly outcome.
   * Uses /status refresh as the source of truth for resolved discount effects.
   *
   * `kind` ('coupon' | 'referral') only controls the user-facing wording — both
   * flows hit the same redeem endpoint, but each tab must speak in its own terms.
   */
  const applyPromoCode = async (codeInput, { kind = 'coupon' } = {}) => {
    const noun = codeNounFor(kind);
    const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

    const code = String(codeInput || '').trim();
    if (!code) {
      return { type: 'error', message: `Please enter a ${noun}.` };
    }

    const normalizedCode = normalizePromoCode(code);
    const discountBefore = JSON.stringify(discount.value ?? null);

    try {
      const referralProgram = useReferralProgram();
      const data = await referralProgram.redeemReferralCode(code);
      if (!data || typeof data !== 'object') {
        throw new Error('Unexpected response from redeem endpoint');
      }

      const hint = relabelCodeNoun(data?.eligibility_hint, kind) || undefined;

      // Billing /status is the source of truth for the resolved discount view.
      await refreshBillingStatus();

      const status = typeof data.status === 'string' ? data.status.toLowerCase() : null;
      if (status === 'rejected') {
        return {
          type: 'error',
          message: data?.reason ? relabelCodeNoun(data.reason, kind) : `This ${noun} is invalid or not eligible.`,
          hint,
        };
      }

      if (data?.already_redeemed || status === 'already_redeemed') {
        return { type: 'info', message: `This ${noun} is already on your account.`, hint };
      }

      const discountAfter = JSON.stringify(discount.value ?? null);
      const hasAppliedCode = appliedCodes.value.some(
        (entry) => normalizePromoCode(entry?.code) === normalizedCode,
      );
      const applied =
        status === 'applied'
        || data?.discount_display?.discount_value != null
        || hasAppliedCode
        || discountAfter !== discountBefore;

      if (applied) {
        const pct = data?.discount_display?.discount_value ?? discount.value?.discount_value;
        return { type: 'success', message: pct ? `${pct}% off applied` : `${Noun} applied`, hint };
      }

      return {
        type: 'warning',
        message: `This ${noun} was accepted but no discount was applied.`,
        hint: hint || `Please try another ${noun} or contact support.`,
      };
    } catch (error) {
      return {
        type: 'error',
        message:
          relabelCodeNoun(error.response?.data?.detail, kind)
          || error.message
          || `Could not apply this ${noun}. Please try again.`,
      };
    }
  };

  return {
    // State
    currentPlan,
    subscriptionStatus,
    trialEndDate,
    gracePeriodEnd,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    cancelAt,
    activeAddOns,
    discount,
    billingHistory,
    billingHistoryLoading,
    isLoading,
    isProcessing,
    lastError,
    hasInitialized,

    // Computed
    businessProfileId,
    isTrialActive,
    trialDaysRemaining,
    trialProgressPct,
    isInGracePeriod,
    graceDaysRemaining,
    currentPlanData,
    monthlySubscriptionCost,
    estimatedMonthlyUsageCost,
    totalEstimatedMonthlyCost,
    meteredEstimate,
    meteredLastBilled,
    meteredRateBps,
    meteredStart,
    meteredMtdCost,
    meteredRatePct,
    meteredLastBilledTotal,
    meteredBreakdown,
    meteredEstimateBreakdown,
    isDunning,
    needsSubscription,
    availableAddOns,
    discountLabel,
    discountDurationLabel,
    appliedCodes,
    firstInvoiceDiscountValue,
    hasFirstInvoiceUplift,
    applyDiscount,

    // Actions
    clearError,
    resetBillingHistory,
    initializeBillingData,
    ensureInitialized,
    refreshBillingStatus,
    convertTrialToPaid,
    addAddOn,
    removeAddOn,
    cancelSubscription,
    resumeSubscription,
    fetchBillingHistory,
    redirectToBillingPortal,
    applyPromoCode,
  };
});
